import * as React from 'react';
import { VideoWrapper } from '../VideoClickableWrapper';
import { OpenVideoOverlayTrigger } from '../OpenVideoOverlayTrigger';
import { VideoOverlay } from '../VideoOverlay';
import { getYoutubeIdFromUrl, getYoutubeStartTimeFromUrl } from '../../utils';
import { VideoPreview } from '../VideoPreview';
import { YoutubePlayer } from './YoutubePlayer';

const YOUTUBE_API_SRC = 'https://www.youtube.com/iframe_api';

const useInitYoutubeIframeApi = () => {
  const isApiReady =
    Boolean(window.YT?.Player) && window.YT.Player instanceof Function;

  React.useEffect(() => {
    if (isApiReady) return;
    //Based on the official documentation: https://developers.google.com/youtube/iframe_api_reference
    const tag = document.createElement('script');
    tag.src = YOUTUBE_API_SRC;
    const firstScriptTag = document.getElementsByTagName('script')[0];
    if (firstScriptTag && firstScriptTag.parentNode) {
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  }, [isApiReady]);

  return isApiReady;
};

interface YoutubeVideoProps {
  src: string;
  shouldShowPreview: boolean;
  readonly: boolean;
}

export const YoutubeVideo = ({
  src,
  shouldShowPreview,
  readonly,
}: YoutubeVideoProps) => {
  const isApiReady = useInitYoutubeIframeApi();

  const videoId = getYoutubeIdFromUrl(src);
  if (!videoId) return null;

  if (shouldShowPreview) {
    return <VideoPreview videoId={videoId} />;
  }

  let videoUrl = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&modestbranding=1&enablejsapi=1`;
  const startingTime = getYoutubeStartTimeFromUrl(src);
  if (startingTime) {
    videoUrl = `${videoUrl}&start=${startingTime}`;
  }

  if (readonly) {
    return (
      <VideoWrapper>
        <iframe
          width="100%"
          height="100%"
          src={videoUrl}
          sandbox="allow-scripts allow-same-origin allow-presentation"
        />
      </VideoWrapper>
    );
  }

  return (
    <>
      <VideoWrapper>
        <OpenVideoOverlayTrigger />
        <iframe
          width="100%"
          height="100%"
          src={videoUrl}
          sandbox="allow-scripts allow-same-origin allow-presentation"
        />
      </VideoWrapper>
      <VideoOverlay>
        <YoutubePlayer videoUrl={videoUrl} shouldRun={isApiReady} />
      </VideoOverlay>
    </>
  );
};
